
import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../../../domain/types';

interface FlashDealsSectionProps {
    products: Product[];
    discount: number;
    onAdd: (p: Product) => void;
}

export const FlashDealsSection = memo(({ products, discount, onAdd }: FlashDealsSectionProps) => {
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    const checkScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
        }
    };

    const SCROLL_AMOUNT = 200;

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = SCROLL_AMOUNT;
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

useEffect(() => {
    let ticking = false;
    const throttledCheckScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    throttledCheckScroll();
    window.addEventListener('resize', throttledCheckScroll);
    return () => window.removeEventListener('resize', throttledCheckScroll);
  }, [products]);

    if (products.length === 0) return null;

    const Separator = () => (
        <div className="flex items-center gap-4 px-4 py-2">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent w-full opacity-50"></div>
            <div className="shrink-0 flex gap-1">
                <span className="w-1 h-1 rounded-full bg-orange-500"></span>
                <span className="w-1 h-1 rounded-full bg-orange-500"></span>
                <span className="w-1 h-1 rounded-full bg-orange-500"></span>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent w-full opacity-50"></div>
        </div>
    );

    return (
        <>
            <Separator />
            <div className="mt-2 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 bg-[length:200%_auto] animate-marquee py-6 px-4 mb-4 relative group/flash shadow-xl overflow-hidden rounded-2xl mx-2">
                {/* Decorative Shimmer Effect */}
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer pointer-events-none"></div>
                
                <div className="flex items-center justify-between mb-4 text-white relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center animate-bolt-shake shadow-lg">
                            <span className="material-symbols-outlined filled text-yellow-300 text-3xl drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]">bolt</span>
                        </div>
                        <div>
                            <h3 className="text-2xl font-black italic uppercase leading-none tracking-tighter">
                                Ofertas <span className="text-yellow-300">Flash</span>
                            </h3>
                            <div className="flex items-center gap-1.5 mt-1 bg-black/20 backdrop-blur-md px-2 py-0.5 rounded-full w-fit">
                                <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse"></span>
                                <p className="text-[10px] font-black uppercase tracking-widest">Termina en 04h 23m</p>
                            </div>
                        </div>
                    </div>
                    <button 
                        onClick={() => navigate('/catalog')} 
                        className="bg-white text-red-600 px-4 py-2 rounded-xl text-xs font-black uppercase shadow-lg active:scale-95 transition-all hover:bg-yellow-300 hover:text-red-700"
                    >
                        Ver todo
                    </button>
                </div>
                
                <div className="relative z-10">
                    <button 
                        onClick={() => scroll('left')}
                        className={`absolute -left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/95 dark:bg-black/90 backdrop-blur-md rounded-full shadow-2xl text-red-600 flex items-center justify-center transition-all active:scale-95 hover:bg-white ${showLeftArrow ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}
                    >
                        <span className="material-symbols-outlined text-2xl font-bold">chevron_left</span>
                    </button>

                    <button 
                        onClick={() => scroll('right')}
                        className={`absolute -right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/95 dark:bg-black/90 backdrop-blur-md rounded-full shadow-2xl text-red-600 flex items-center justify-center transition-all active:scale-95 hover:bg-white ${showRightArrow ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}
                    >
                        <span className="material-symbols-outlined text-2xl font-bold">chevron_right</span>
                    </button>

                    <div ref={scrollRef} onScroll={checkScroll} className="flex overflow-x-auto no-scrollbar gap-4 pb-2 scroll-smooth snap-x snap-mandatory px-1">
                        {products.map(product => {
                            const finalPrice = Math.floor(product.price * (1 - discount / 100));
                            return (
                                <div key={product.id} onClick={() => navigate(`/product/${product.id}`)} className="min-w-[160px] w-[160px] bg-surface-light dark:bg-surface-dark rounded-2xl p-3 shadow-xl flex flex-col cursor-pointer active:scale-95 transition-all hover:translate-y-[-4px] snap-start relative border border-white/10">
                                    <div className="w-full aspect-square bg-cover bg-center rounded-xl mb-3 relative overflow-hidden group/img" style={{backgroundImage: `url("${product.imageUrl}")`}}>
                                        <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/5 transition-colors"></div>
                                        <span className="absolute top-2 left-2 bg-yellow-400 text-red-700 text-[10px] font-black px-2 py-0.5 rounded-lg shadow-md z-10 animate-bounce-slow">
                                            -{discount}%
                                        </span>
                                    </div>
                                    <p className="text-text-main dark:text-white font-black text-sm leading-tight line-clamp-1 mb-1">{product.title}</p>
                                    <div className="flex flex-col gap-0 mb-3">
                                        <span className="text-red-600 dark:text-red-500 font-black text-lg leading-none">$ {(finalPrice || 0).toLocaleString()}</span>
                                        <span className="text-[10px] text-gray-400 line-through decoration-red-500/50 mt-1">$ {(product.price || 0).toLocaleString()}</span>
                                    </div>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); onAdd(product); }} 
                                        className="mt-auto w-full bg-red-600 text-white hover:bg-red-700 text-[10px] font-black uppercase py-2.5 rounded-xl transition-all flex items-center justify-center gap-1 shadow-md shadow-red-500/20 active:scale-95"
                                    >
                                        Agregar <span className="material-symbols-outlined text-[14px] filled">add_shopping_cart</span>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
});
FlashDealsSection.displayName = 'FlashDealsSection';
